import { useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Phone, Download, Video, ArrowRight, Plus, Minus,
  MapPin, Plane, Waves, Route, Crosshair, ChevronDown,
} from "lucide-react";
import { SITE } from "../lib/data";
import {
  NRI_SITE, WHY_STRIP, PERFORMANCE_STATS, CURRENCIES, FX_TO_INR,
  SHOWCASE, CINEMATIC, MAP_POINTS, LIFESTYLE,
  TESTIMONIALS, FAQ,
} from "../lib/nriData";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const WhatsAppIcon = (props) => (
  <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true" {...props}>
    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347zM12.04 21.785h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.889-9.884 2.641 0 5.124 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.886 9.884zm8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 0 0-3.49-8.413z" />
  </svg>
);

const goto = (id) => () => {
  const el = document.getElementById(id);
  if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
};

const Eyebrow = ({ children }) => (
  <p className="text-[11px] tracking-[0.36em] uppercase text-[#987F53] font-semibold mb-6">{children}</p>
);
const H2 = ({ children, className = "" }) => (
  <h2 className={`font-serif-display text-4xl md:text-5xl lg:text-6xl text-[#1A362D] tracking-tight font-light leading-[1.05] ${className}`}>
    {children}
  </h2>
);

// -------- Header (light) --------
function NriHeader({ onSchedule }) {
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const on = () => setScrolled(window.scrollY > 32);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <header
      data-testid="nri-header"
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? "bg-[#F7F5F0]/92 backdrop-blur-md border-b border-[#1A362D]/10" : "bg-transparent"
      }`}
    >
      <div className="max-w-[100rem] mx-auto px-6 md:px-10 lg:px-16 flex items-center justify-between py-5 md:py-6">
        <Link to="/nri" data-testid="nri-header-logo" className="flex items-center gap-3">
          <img
            src={SITE.logo}
            alt="Amarja Hills"
            className={`h-10 md:h-12 w-auto transition-all ${scrolled ? "" : "logo-on-dark"}`}
          />
        </Link>
        <nav className={`hidden md:flex items-center gap-8 text-[11px] tracking-[0.24em] uppercase font-medium ${scrolled ? "text-[#1A362D]/80" : "text-[#F7F5F0]/90"}`}>
          <button onClick={goto("nri-performance")} className={`transition-colors ${scrolled ? "hover:text-[#987F53]" : "hover:text-[#E2CFA1]"}`}>Performance</button>
          <button onClick={goto("nri-lifestyle")} className={`transition-colors ${scrolled ? "hover:text-[#987F53]" : "hover:text-[#E2CFA1]"}`}>Lifestyle</button>
          <button onClick={goto("nri-map")} className={`transition-colors ${scrolled ? "hover:text-[#987F53]" : "hover:text-[#E2CFA1]"}`}>Location</button>
          <button onClick={goto("nri-faq")} className={`transition-colors ${scrolled ? "hover:text-[#987F53]" : "hover:text-[#E2CFA1]"}`}>FAQ</button>
        </nav>
        <div className="flex items-center gap-3">
          <a
            data-testid="nri-header-brochure"
            href={NRI_SITE.brochureUrl}
            target="_blank"
            rel="noopener noreferrer"
            className={`hidden sm:inline-flex items-center gap-2 text-[11px] tracking-[0.22em] uppercase transition-colors border-b pb-0.5 ${
              scrolled
                ? "text-[#987F53] hover:text-[#1A362D] border-[#987F53]/40 hover:border-[#1A362D]"
                : "text-[#E2CFA1] hover:text-[#F7F5F0] border-[#E2CFA1]/50 hover:border-[#F7F5F0]"
            }`}
          >
            Brochure <Download className="w-3.5 h-3.5" strokeWidth={1.8} />
          </a>
          <button
            data-testid="nri-header-schedule"
            onClick={onSchedule}
            className={`inline-flex items-center gap-2 px-5 py-2.5 md:px-6 md:py-3 text-[11px] tracking-[0.22em] uppercase font-semibold transition-colors ${
              scrolled
                ? "bg-[#1A362D] text-[#F7F5F0] hover:bg-[#987F53]"
                : "bg-[#F7F5F0] text-[#1A362D] hover:bg-[#E2CFA1]"
            }`}
          >
            Schedule Consultation
          </button>
        </div>
      </div>
    </header>
  );
}

// -------- Hero — matches main site's proportions --------
function NriHero({ onSchedule }) {
  const videoRef = useRef(null);
  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    const reduce = window.matchMedia("(prefers-reduced-motion: reduce)").matches;
    if (reduce) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) {
          try { v.load(); } catch (_e) { /* noop */ }
          const p = v.play();
          if (p && typeof p.catch === "function") p.catch(() => {});
        } else v.pause();
      }),
      { rootMargin: "0px", threshold: 0.05 }
    );
    io.observe(v);
    return () => io.disconnect();
  }, []);

  return (
    <section
      data-testid="nri-hero"
      className="relative min-h-[100svh] w-full overflow-hidden bg-[#0B0F0D]"
    >
      <div className="absolute inset-0 overflow-hidden">
        <video
          ref={videoRef}
          data-testid="nri-hero-video"
          autoPlay muted loop playsInline
          // eslint-disable-next-line react/no-unknown-property
          webkit-playsinline="true"
          disablePictureInPicture
          controls={false}
          preload="none"
          poster="/img/site/drone1.jpg"
          aria-hidden="true"
          className="absolute top-1/2 left-1/2 w-auto h-auto min-w-[110%] min-h-[110%] object-cover pointer-events-none"
          style={{ transform: "translate(-50%, -50%) scale(1.1)" }}
        >
          <source src="/hero-mobile.mp4" media="(max-width: 768px)" type="video/mp4" />
          <source src="/hero.mp4" type="video/mp4" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-[#0A0F0C]/50 via-[#0A0F0C]/25 to-[#0A0F0C]/90" />
      </div>

      <div className="relative z-10 min-h-[100svh] flex flex-col justify-between px-6 sm:px-10 lg:px-16 pt-36 sm:pt-40 lg:pt-48 pb-12 lg:pb-16">
        <div className="max-w-6xl">
          <p
            data-testid="nri-hero-eyebrow"
            className="text-[10px] sm:text-[11px] tracking-[0.4em] uppercase text-[#E2CFA1] font-semibold mb-6 sm:mb-8"
          >
            For Global Indians
          </p>
          <h1
            data-testid="nri-hero-title"
            className="font-serif-display text-[#F7F5F0] text-[2.75rem] sm:text-6xl md:text-7xl lg:text-[7.5rem] leading-[0.95] tracking-tighter font-light"
          >
            Own a Legacy Estate in India.
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            <em className="font-normal text-[#E2CFA1]">Built for Generations.</em>
          </h1>
          <p
            data-testid="nri-hero-subtitle"
            className="mt-7 sm:mt-10 max-w-2xl text-[#F7F5F0]/90 text-base sm:text-lg md:text-xl font-light leading-relaxed"
          >
            500 acres overlooking Pawna Lake
            <span className="mx-3 text-[#E2CFA1]/70">|</span>
            2 hours from Mumbai
          </p>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 sm:gap-8">
          <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4">
            <button
              data-testid="nri-hero-cta-consult"
              onClick={onSchedule}
              className="btn-ghost bg-[#F7F5F0] text-[#1A362D] border-[#F7F5F0] hover:bg-[#987F53] hover:text-[#F7F5F0] hover:border-[#987F53] w-full sm:w-auto"
            >
              Schedule Consultation
            </button>
            <a
              data-testid="nri-hero-cta-brochure"
              href={NRI_SITE.brochureUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-ghost w-full sm:w-auto"
            >
              <Download className="w-4 h-4" />
              Download Brochure
            </a>
          </div>

          <button
            data-testid="nri-hero-scroll"
            onClick={goto("nri-why")}
            className="hidden md:flex items-center gap-2 text-[10px] tracking-[0.3em] uppercase text-[#F7F5F0]/70 hover:text-[#F7F5F0]"
          >
            The Global Case
            <ChevronDown className="w-4 h-4 animate-bounce" />
          </button>
        </div>
      </div>
    </section>
  );
}

// -------- Why Strip (editorial numbered, ivory) --------
function NriWhyStrip() {
  return (
    <section
      id="nri-why"
      data-testid="nri-why"
      className="relative py-20 md:py-28 bg-[#F7F5F0]"
    >
      <div className="max-w-[100rem] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-3xl mb-14 md:mb-16">
          <Eyebrow>Why Global Indians Are Choosing Amarja</Eyebrow>
          <H2>Four Quiet Reasons.<br /><em className="font-normal text-[#987F53]">One Enduring Estate.</em></H2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8 md:gap-10 lg:gap-12">
          {WHY_STRIP.map((s, i) => (
            <div
              key={s.label}
              data-testid={`nri-why-card-${i}`}
              className="pt-8 border-t border-[#1A362D]/15 flex flex-col"
            >
              <p className="font-serif-display italic text-3xl md:text-4xl text-[#987F53] font-light leading-none">
                0{i + 1}
              </p>
              <p className="mt-6 font-serif-display text-2xl md:text-3xl text-[#1A362D] font-light leading-tight">
                {s.label}
              </p>
              <p className="mt-4 text-sm text-[#1A362D]/60 leading-relaxed font-light">
                {[
                  "A tangible holding your children will hold in their name — beyond stocks or funds.",
                  "A rupee-denominated asset in one of the world's fastest-growing economies.",
                  "500 contiguous hill acres within 2 hours of Mumbai. There is a very short list.",
                  "A private residence to come home to — for winters, weddings, or good.",
                ][i]}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- Cinematic Estate Showcase (ivory) --------
function NriShowcase() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % SHOWCASE.length), 5500);
    return () => clearInterval(t);
  }, []);
  return (
    <section
      id="nri-showcase"
      data-testid="nri-showcase"
      className="relative py-20 md:py-28 bg-[#F7F5F0]"
    >
      <div className="max-w-[100rem] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-3xl mb-10 md:mb-14">
          <Eyebrow>Homes Delivered · Since 2018</Eyebrow>
          <H2>Built by Amarja.<br /><em className="font-normal text-[#987F53]">Chosen by Global Indians.</em></H2>
        </div>

        <div className="relative aspect-[16/9] md:aspect-[21/9] overflow-hidden border border-[#1A362D]/10 bg-[#1A362D]">
          {SHOWCASE.map((s, i) => (
            <div
              key={s.img}
              className={`absolute inset-0 transition-opacity duration-[1400ms] ${idx === i ? "opacity-100" : "opacity-0"}`}
            >
              <img
                src={s.img}
                alt={s.caption}
                className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[7000ms] ${idx === i ? "scale-105" : "scale-100"}`}
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F0C]/75 via-transparent to-transparent" />
              <div className="absolute bottom-0 left-0 right-0 p-6 md:p-10 lg:p-14">
                <p className="text-[10px] md:text-[11px] tracking-[0.32em] uppercase text-[#E2CFA1] font-semibold mb-3">
                  {String(i + 1).padStart(2, "0")} / {String(SHOWCASE.length).padStart(2, "0")}
                </p>
                <p className="font-serif-display text-3xl md:text-5xl text-[#F7F5F0] font-light leading-tight">
                  {s.caption}
                </p>
              </div>
            </div>
          ))}
        </div>

        <div className="mt-6 grid grid-cols-4 md:grid-cols-7 gap-2 md:gap-3">
          {SHOWCASE.map((s, i) => (
            <button
              key={`thumb-${i}`}
              data-testid={`nri-showcase-thumb-${i}`}
              onClick={() => setIdx(i)}
              className={`relative aspect-[4/3] overflow-hidden border transition-all ${
                idx === i ? "border-[#987F53]" : "border-[#1A362D]/10 opacity-55 hover:opacity-90"
              }`}
              aria-label={`Show ${s.caption}`}
            >
              <img src={s.img} alt="" className="absolute inset-0 w-full h-full object-cover" />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- Animated counter --------
function useCounter(target, active, duration = 1800) {
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!active) return;
    let start, raf;
    const step = (t) => {
      if (!start) start = t;
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(target * eased);
      if (p < 1) raf = requestAnimationFrame(step);
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
  }, [active, target, duration]);
  return n;
}

function StatCard({ stat, active, idx }) {
  const n = useCounter(stat.value, active);
  // Show range as final display value when 90% of animation is done
  const showRange = stat.range && n >= stat.value * 0.9;
  const display = showRange ? stat.range : `${Math.round(n)}${stat.suffix || ""}`;
  return (
    <div data-testid={`nri-stat-${idx}`} className="text-center px-4">
      <div className="font-serif-display text-6xl md:text-8xl lg:text-[112px] text-[#987F53] font-light leading-none tracking-tight">
        {display}
      </div>
      <p className="mt-6 text-[11px] tracking-[0.32em] uppercase text-[#1A362D] font-semibold">
        {stat.label}
      </p>
      <p className="mt-3 text-sm text-[#1A362D]/60 font-light">{stat.caption}</p>
    </div>
  );
}

function NriPerformance() {
  const ref = useRef(null);
  const [active, setActive] = useState(false);
  useEffect(() => {
    if (!ref.current) return;
    const io = new IntersectionObserver(([e]) => e.isIntersecting && setActive(true), { threshold: 0.3 });
    io.observe(ref.current);
    return () => io.disconnect();
  }, []);
  return (
    <section
      id="nri-performance"
      ref={ref}
      data-testid="nri-performance"
      className="relative py-24 md:py-32 bg-[#EFEAE0] border-y border-[#1A362D]/10"
    >
      <div className="max-w-[100rem] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-3xl mb-16 md:mb-20">
          <Eyebrow>Investment Performance</Eyebrow>
          <H2>Numbers That<br /><em className="font-normal text-[#987F53]">Speak Quietly.</em></H2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-16 md:gap-8 items-start">
          {PERFORMANCE_STATS.map((s, i) => (
            <StatCard key={s.label} stat={s} active={active} idx={i} />
          ))}
        </div>
        <p
          data-testid="nri-performance-disclaimer"
          className="mt-16 text-xs text-[#1A362D]/45 max-w-2xl mx-auto text-center leading-relaxed font-light"
        >
          Illustrative. Past performance is not indicative of future returns.
        </p>
      </div>
    </section>
  );
}

// -------- Currency Calculator (ivory) --------
function NriCurrencyCalculator() {
  const [currency, setCurrency] = useState("USD");
  const [amount, setAmount] = useState(500000);
  const acres = useMemo(() => {
    const inr = Number(amount) * (FX_TO_INR[currency] || 1);
    const perAcre = CURRENCIES.find((c) => c.code === currency)?.perAcreInr || 22500000;
    if (!perAcre || !inr) return { low: 0, high: 0 };
    const raw = inr / perAcre;
    return { low: Math.max(0, raw * 0.85), high: raw * 1.05 };
  }, [amount, currency]);
  const fmt = (n) => (n < 1 ? n.toFixed(2) : n.toFixed(1));

  return (
    <section
      id="nri-calculator"
      data-testid="nri-calculator"
      className="relative py-24 md:py-32 bg-[#F7F5F0]"
    >
      <div className="max-w-[100rem] mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-5">
          <Eyebrow>Currency Explorer</Eyebrow>
          <H2 className="mb-6">Your Budget,<br /><em className="font-normal text-[#987F53]">In Acres.</em></H2>
          <p className="text-[#1A362D]/70 text-lg font-light leading-relaxed max-w-lg">
            Actual pricing is shared privately with qualified enquiries.
          </p>
        </div>

        <div
          data-testid="nri-calc-card"
          className="lg:col-span-7 bg-white border border-[#1A362D]/10 p-8 md:p-12 shadow-[0_30px_80px_-40px_rgba(26,54,45,0.25)]"
        >
          <div className="grid md:grid-cols-2 gap-8 mb-10">
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-[#987F53] font-semibold block mb-3">Currency</label>
              <select
                data-testid="nri-calc-currency"
                value={currency}
                onChange={(e) => setCurrency(e.target.value)}
                className="w-full bg-transparent border-b border-[#1A362D]/22 focus:border-[#987F53] text-[#1A362D] py-3 text-lg font-light outline-none appearance-none cursor-pointer"
              >
                {CURRENCIES.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {c.label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="text-[10px] tracking-[0.28em] uppercase text-[#987F53] font-semibold block mb-3">Budget</label>
              <div className="flex items-baseline gap-3 border-b border-[#1A362D]/22 py-3">
                <span className="text-[#987F53] text-lg font-light">
                  {CURRENCIES.find((c) => c.code === currency)?.symbol}
                </span>
                <input
                  data-testid="nri-calc-amount"
                  type="number"
                  min="0"
                  step="10000"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  className="flex-1 bg-transparent text-[#1A362D] text-2xl font-light outline-none"
                />
              </div>
            </div>
          </div>

          <div className="pt-4 border-t border-[#1A362D]/10">
            <p className="text-[10px] tracking-[0.28em] uppercase text-[#1A362D]/55 font-semibold mb-3">Estimate</p>
            <p
              data-testid="nri-calc-result"
              className="font-serif-display text-3xl md:text-4xl text-[#1A362D] font-light leading-tight"
            >
              Approximately{" "}
              <span className="text-[#987F53]">{fmt(acres.low)}–{fmt(acres.high)} acres</span>
            </p>
            <p className="mt-3 text-xs text-[#1A362D]/45 font-light">Indicative only. Actual pricing shared privately with qualified buyers.</p>
          </div>
        </div>
      </div>
    </section>
  );
}

// -------- Full-bleed Cinematic Gallery --------
function NriCinematicGallery() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % CINEMATIC.length), 5000);
    return () => clearInterval(t);
  }, []);
  return (
    <section
      id="nri-cinematic"
      data-testid="nri-cinematic"
      className="relative h-[85vh] md:h-screen w-full overflow-hidden bg-[#0A0F0C]"
    >
      {CINEMATIC.map((c, i) => (
        <div
          key={c.img}
          className={`absolute inset-0 transition-opacity duration-[1500ms] ${idx === i ? "opacity-100" : "opacity-0"}`}
        >
          <img
            src={c.img}
            alt={c.title}
            className={`absolute inset-0 w-full h-full object-cover transition-transform duration-[8000ms] ${idx === i ? "scale-110" : "scale-100"}`}
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F0C]/80 via-[#0A0F0C]/20 to-transparent" />
        </div>
      ))}
      <div className="relative z-10 h-full flex items-end pb-24 md:pb-32">
        <div className="max-w-[100rem] mx-auto px-6 md:px-10 lg:px-16 w-full">
          <div className="max-w-3xl">
            <p className="text-[10px] md:text-[11px] tracking-[0.36em] uppercase text-[#E2CFA1] font-semibold mb-4">
              {String(idx + 1).padStart(2, "0")} / {String(CINEMATIC.length).padStart(2, "0")}
            </p>
            <h3
              data-testid="nri-cinematic-title"
              className="font-serif-display text-5xl md:text-7xl lg:text-[92px] text-[#F7F5F0] font-light leading-[0.98] tracking-tight"
            >
              {CINEMATIC[idx].title}
            </h3>
            <p className="mt-4 md:mt-6 font-serif-display italic text-xl md:text-2xl text-[#E2CFA1] font-light">
              {CINEMATIC[idx].sub}
            </p>
          </div>
          <div className="mt-10 flex gap-2">
            {CINEMATIC.map((_, i) => (
              <button
                key={i}
                data-testid={`nri-cinematic-dot-${i}`}
                onClick={() => setIdx(i)}
                aria-label={`Slide ${i + 1}`}
                className={`h-[2px] transition-all ${idx === i ? "w-14 bg-[#E2CFA1]" : "w-8 bg-[#F7F5F0]/25 hover:bg-[#F7F5F0]/50"}`}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// -------- Interactive Map (ivory) --------
const iconFor = (type) => {
  const cls = "w-4 h-4";
  if (type === "airport") return <Plane className={cls} strokeWidth={1.6} />;
  if (type === "lake") return <Waves className={cls} strokeWidth={1.6} />;
  if (type === "road") return <Route className={cls} strokeWidth={1.6} />;
  if (type === "helipad") return <Crosshair className={cls} strokeWidth={1.6} />;
  return <MapPin className={cls} strokeWidth={1.6} />;
};

function NriMap() {
  const [active, setActive] = useState(3);
  return (
    <section id="nri-map" data-testid="nri-map" className="relative py-24 md:py-32 bg-[#F7F5F0]">
      <div className="max-w-[100rem] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-3xl mb-14">
          <Eyebrow>Location · Sahyadri</Eyebrow>
          <H2>Two Hours from Mumbai.<br /><em className="font-normal text-[#987F53]">A World Away.</em></H2>
        </div>
        <div className="grid lg:grid-cols-12 gap-8 items-center">
          <div className="lg:col-span-7 relative aspect-[4/3] bg-[#1A362D] border border-[#1A362D]/10 overflow-hidden">
            <img src="/img/site/drone3.jpg" alt="" className="absolute inset-0 w-full h-full object-cover opacity-35" />
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,transparent_10%,#1A362D_85%)]" />
            <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 z-10">
              <div className="relative flex items-center justify-center">
                <span className="absolute inset-0 w-16 h-16 -translate-x-1/2 -translate-y-1/2 left-1/2 top-1/2 rounded-full border border-[#E2CFA1]/40 animate-ping" />
                <div className="relative bg-[#E2CFA1] text-[#1A362D] px-4 py-2 text-[10px] tracking-[0.28em] uppercase font-bold">
                  Amarja Hills
                </div>
              </div>
            </div>
            {MAP_POINTS.map((p, i) => {
              const positions = [
                { top: "15%", left: "12%" },
                { top: "72%", left: "85%" },
                { top: "22%", left: "76%" },
                { top: "58%", left: "42%" },
                { top: "38%", left: "22%" },
                { top: "48%", left: "62%" },
              ][i];
              return (
                <button
                  key={p.label}
                  data-testid={`nri-map-point-${i}`}
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onClick={() => setActive(i)}
                  style={positions}
                  className={`absolute z-20 flex items-center gap-1.5 px-2 py-1 text-[10px] uppercase tracking-[0.2em] font-semibold transition-all ${
                    active === i ? "bg-[#F7F5F0] text-[#1A362D]" : "bg-[#1A362D]/60 border border-[#E2CFA1]/25 text-[#E2CFA1] hover:bg-[#1A362D]"
                  }`}
                >
                  {iconFor(p.type)} {p.label}
                </button>
              );
            })}
          </div>

          <div className="lg:col-span-5 space-y-2">
            {MAP_POINTS.map((p, i) => (
              <button
                key={p.label}
                data-testid={`nri-map-item-${i}`}
                onMouseEnter={() => setActive(i)}
                onFocus={() => setActive(i)}
                onClick={() => setActive(i)}
                className={`w-full text-left flex items-start gap-4 p-4 border transition-all ${
                  active === i ? "border-[#987F53]/60 bg-white" : "border-[#1A362D]/10 hover:border-[#987F53]/40"
                }`}
              >
                <span className={`mt-1 w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-colors ${
                  active === i ? "bg-[#987F53] text-[#F7F5F0]" : "bg-[#987F53]/10 text-[#987F53]"
                }`}>
                  {iconFor(p.type)}
                </span>
                <div>
                  <p className="font-serif-display text-xl text-[#1A362D] font-light leading-tight">{p.label}</p>
                  <p className="text-sm text-[#1A362D]/55 mt-1 font-light">{p.detail}</p>
                </div>
              </button>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

// -------- Lifestyle bento (ivory) --------
function NriLifestyle() {
  return (
    <section id="nri-lifestyle" data-testid="nri-lifestyle" className="relative py-24 md:py-32 bg-[#EFEAE0] border-y border-[#1A362D]/10">
      <div className="max-w-[100rem] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-3xl mb-14">
          <Eyebrow>Lifestyle</Eyebrow>
          <H2>Days That Feel<br /><em className="font-normal text-[#987F53]">Longer Than They Are.</em></H2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6">
          {LIFESTYLE.map((l, i) => (
            <article
              key={l.title}
              data-testid={`nri-lifestyle-card-${i}`}
              className="group relative aspect-[4/5] overflow-hidden bg-[#1A362D] border border-[#1A362D]/10"
            >
              <img
                src={l.img}
                alt={l.title}
                loading="lazy"
                className="absolute inset-0 w-full h-full object-cover opacity-85 group-hover:opacity-100 group-hover:scale-105 transition-all duration-[900ms]"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0F0C]/85 via-[#0A0F0C]/20 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-6 md:p-8">
                <h3 className="font-serif-display text-2xl md:text-3xl text-[#F7F5F0] font-light leading-tight">
                  {l.title}
                </h3>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- Testimonials (ivory) --------
function NriTestimonials() {
  return (
    <section id="nri-testimonials" data-testid="nri-testimonials" className="relative py-24 md:py-32 bg-[#F7F5F0]">
      <div className="max-w-[100rem] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-3xl mb-14">
          <Eyebrow>Voices of Owners</Eyebrow>
          <H2>From Dubai to Sydney,<br /><em className="font-normal text-[#987F53]">A Shared Instinct.</em></H2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {TESTIMONIALS.map((t, i) => (
            <figure
              key={t.name}
              data-testid={`nri-testimonial-${i}`}
              className="bg-white border border-[#1A362D]/10 p-8 flex flex-col"
            >
              <blockquote className="font-serif-display text-xl md:text-2xl text-[#1A362D] font-light italic leading-snug flex-1">
                “{t.quote}”
              </blockquote>
              <figcaption className="mt-6 flex items-center gap-4">
                <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#E2CFA1] to-[#987F53] flex items-center justify-center font-serif-display text-lg text-[#1A362D] font-light">
                  {t.initial}
                </div>
                <div>
                  <p className="font-serif-display text-base text-[#1A362D] font-light">{t.name}</p>
                  <p className="text-[10px] tracking-[0.22em] uppercase text-[#987F53] font-semibold mt-0.5">{t.city}</p>
                </div>
              </figcaption>
            </figure>
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- FAQ (ivory) --------
function FaqItem({ item, open, onToggle, idx }) {
  return (
    <div data-testid={`nri-faq-${idx}`} className="border-b border-[#1A362D]/12">
      <button
        onClick={onToggle}
        className="w-full text-left py-6 flex items-start gap-6 group"
        aria-expanded={open}
      >
        <span className="font-serif-display italic text-[#987F53] font-light text-lg w-10 flex-shrink-0 mt-1">
          {String(idx + 1).padStart(2, "0")}
        </span>
        <span className="flex-1 font-serif-display text-xl md:text-2xl text-[#1A362D] font-light leading-snug group-hover:text-[#987F53] transition-colors">
          {item.q}
        </span>
        <span className="flex-shrink-0 mt-2 text-[#987F53]">
          {open ? <Minus className="w-5 h-5" /> : <Plus className="w-5 h-5" />}
        </span>
      </button>
      <div className={`grid transition-all duration-500 ${open ? "grid-rows-[1fr] opacity-100 pb-6" : "grid-rows-[0fr] opacity-0"}`}>
        <div className="overflow-hidden pl-16 pr-10 text-[#1A362D]/70 text-base font-light leading-relaxed">
          {item.a}
        </div>
      </div>
    </div>
  );
}

function NriFaq() {
  const [open, setOpen] = useState(0);
  return (
    <section id="nri-faq" data-testid="nri-faq" className="relative py-24 md:py-32 bg-[#EFEAE0] border-y border-[#1A362D]/10">
      <div className="max-w-[80rem] mx-auto px-6 md:px-10 lg:px-16">
        <div className="max-w-3xl mb-10">
          <Eyebrow>Questions</Eyebrow>
          <H2>The Details<br /><em className="font-normal text-[#987F53]">You Deserve.</em></H2>
        </div>
        <div>
          {FAQ.map((f, i) => (
            <FaqItem key={f.q} item={f} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} idx={i} />
          ))}
        </div>
      </div>
    </section>
  );
}

// -------- Lead Form — editorial, cleaner --------
const COUNTRIES = [
  "United States", "United Kingdom", "United Arab Emirates", "Singapore",
  "Canada", "Australia", "Germany", "France", "Netherlands", "Switzerland",
  "Hong Kong", "New Zealand", "Qatar", "Saudi Arabia", "Kuwait", "Oman",
  "India (temporarily overseas)", "Other",
];
const TIMEZONES = [
  "Pacific Time (PT · USA West)",
  "Central Time (CT · USA)",
  "Eastern Time (ET · USA East)",
  "Greenwich Mean Time (GMT · UK)",
  "Central European Time (CET)",
  "Gulf Standard Time (GST · UAE)",
  "India Standard Time (IST)",
  "Singapore Time (SGT)",
  "Australian Eastern Time (AET)",
];
const BUDGET_RANGES = [
  "USD 250K – 500K",
  "USD 500K – 1M",
  "USD 1M – 2M",
  "USD 2M – 5M",
  "USD 5M +",
  "Prefer to discuss privately",
];

const Field = ({ label, children }) => (
  <div>
    <label className="text-[10px] tracking-[0.28em] uppercase text-[#987F53] font-semibold block mb-2">{label}</label>
    {children}
  </div>
);

function NriLeadForm({ formRef }) {
  const [form, setForm] = useState({
    name: "", country: "", whatsapp: "", email: "",
    preferred_currency: "USD", preferred_time_zone: "", budget: "",
  });
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || form.name.trim().length < 2) {
      toast.error("Please enter your full name.");
      return;
    }
    const wa = form.whatsapp.replace(/[^\d]/g, "");
    if (wa.length < 6) {
      toast.error("Please enter a valid WhatsApp number with country code.");
      return;
    }
    const email = form.email.trim();
    if (email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      toast.error("That email doesn't look right.");
      return;
    }
    if (!form.country) {
      toast.error("Please select your country of residence.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/enquiries`, {
        name: form.name.trim(),
        phone: form.whatsapp.trim(),
        email: email || null,
        property_interest: "NRI · Legacy Estate Enquiry",
        budget: form.budget || null,
        message: `Preferred time zone: ${form.preferred_time_zone || "—"}`,
        audience: "nri",
        country: form.country,
        preferred_currency: form.preferred_currency,
        preferred_time_zone: form.preferred_time_zone || null,
      });
      try {
        if (typeof window.gtag === "function") {
          window.gtag("event", "generate_lead", { event_category: "lead", event_label: "nri-form", value: 1 });
          if (window.AMARJA_ADS_CONVERSION) {
            window.gtag("event", "conversion", { send_to: window.AMARJA_ADS_CONVERSION, value: 1.0, currency: "INR" });
          }
        }
      } catch (_e) { /* analytics non-critical */ }
      setDone(true);
      toast.success("Thank you — an NRI advisor will be in touch shortly.");
    } catch (err) {
      console.error(err);
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Something went wrong. Please try again.";
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="nri-form" ref={formRef} data-testid="nri-form" className="relative py-24 md:py-32 bg-[#F7F5F0] overflow-hidden">
      <div className="max-w-[100rem] mx-auto px-6 md:px-10 lg:px-16 grid lg:grid-cols-12 gap-14 items-stretch">
        {/* Left panel — image + copy */}
        <div className="lg:col-span-5 relative min-h-[420px]">
          <div className="absolute inset-0 -mx-6 md:-mx-0 lg:-mr-8">
            <img src="/img/site/drone1.jpg" alt="" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#1A362D] via-[#1A362D]/60 to-[#1A362D]/30" />
          </div>
          <div className="relative h-full flex flex-col justify-end p-8 md:p-12 text-[#F7F5F0]">
            <p className="text-[10px] tracking-[0.32em] uppercase text-[#E2CFA1] font-semibold mb-4">Begin Your Legacy</p>
            <h2 className="font-serif-display text-4xl md:text-5xl lg:text-6xl leading-[1.05] font-light">
              Start Your<br /><em className="font-normal text-[#E2CFA1]">India Legacy.</em>
            </h2>
            <p className="mt-6 text-[#F7F5F0]/85 text-base md:text-lg font-light leading-relaxed max-w-md">
              An NRI advisor will personally call you on your time zone within one business day. No obligations. Complete discretion.
            </p>
            <div className="mt-8 space-y-2.5 text-sm text-[#F7F5F0]/80 font-light">
              <p className="flex items-center gap-3"><Phone className="w-4 h-4 text-[#E2CFA1]" /> {SITE.phone}</p>
              <p className="flex items-center gap-3">
                <span className="text-[#E2CFA1]"><WhatsAppIcon className="w-4 h-4" /></span>
                WhatsApp: {SITE.phone}
              </p>
              <p className="flex items-center gap-3">
                <span className="text-[#E2CFA1] text-base leading-none">@</span>
                {SITE.email}
              </p>
            </div>
          </div>
        </div>

        {/* Right — the form */}
        <div className="lg:col-span-7 bg-white border border-[#1A362D]/10 p-8 md:p-12 lg:p-14 shadow-[0_30px_80px_-40px_rgba(26,54,45,0.25)]">
          {done ? (
            <div data-testid="nri-form-success" className="py-6">
              <p className="text-[10px] tracking-[0.28em] uppercase text-[#987F53] font-semibold mb-4">Received</p>
              <p className="font-serif-display text-3xl md:text-4xl text-[#1A362D] font-light leading-tight mb-6">
                Thank you. Your enquiry is in our hands.
              </p>
              <p className="text-[#1A362D]/70 text-base font-light leading-relaxed">
                An advisor will reach out within one business day.
              </p>
              <a
                href={NRI_SITE.brochureUrl}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="nri-form-success-brochure"
                className="mt-8 inline-flex items-center gap-2 text-[#987F53] hover:text-[#1A362D] transition-colors border-b border-[#987F53]/40 pb-0.5 text-[12px] tracking-[0.22em] uppercase font-semibold"
              >
                Download Brochure <Download className="w-4 h-4" />
              </a>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-8" data-testid="nri-form-el">
              {/* Section 1 — You */}
              <div>
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#987F53] font-semibold mb-6">
                  About You
                </p>
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                  <Field label="Full Name *">
                    <input data-testid="nri-form-name" required value={form.name} onChange={update("name")} className="nri-input" placeholder="Your name" />
                  </Field>
                  <Field label="Country of Residence *">
                    <select data-testid="nri-form-country" required value={form.country} onChange={update("country")} className="nri-input">
                      <option value="">Select country</option>
                      {COUNTRIES.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </Field>
                </div>
              </div>

              {/* Section 2 — Contact */}
              <div>
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#987F53] font-semibold mb-6">
                  How We&apos;ll Reach You
                </p>
                <div className="grid md:grid-cols-2 gap-x-8 gap-y-6">
                  <Field label="WhatsApp *">
                    <input data-testid="nri-form-whatsapp" required value={form.whatsapp} onChange={update("whatsapp")} className="nri-input" placeholder="+1 415 …" />
                  </Field>
                  <Field label="Email (optional)">
                    <input data-testid="nri-form-email" type="email" value={form.email} onChange={update("email")} className="nri-input" placeholder="you@company.com" />
                  </Field>
                  <Field label="Preferred Time Zone">
                    <select data-testid="nri-form-tz" value={form.preferred_time_zone} onChange={update("preferred_time_zone")} className="nri-input">
                      <option value="">Any time zone works</option>
                      {TIMEZONES.map((t) => <option key={t} value={t}>{t}</option>)}
                    </select>
                  </Field>
                  <Field label="Preferred Currency">
                    <select data-testid="nri-form-currency" value={form.preferred_currency} onChange={update("preferred_currency")} className="nri-input">
                      {CURRENCIES.map((c) => <option key={c.code} value={c.code}>{c.code} — {c.label}</option>)}
                    </select>
                  </Field>
                </div>
              </div>

              {/* Section 3 — Investment */}
              <div>
                <p className="text-[10px] tracking-[0.32em] uppercase text-[#987F53] font-semibold mb-6">
                  Your Investment Consideration
                </p>
                <Field label="Investment Budget (optional)">
                  <select data-testid="nri-form-budget" value={form.budget} onChange={update("budget")} className="nri-input">
                    <option value="">Prefer to discuss privately</option>
                    {BUDGET_RANGES.map((b) => <option key={b} value={b}>{b}</option>)}
                  </select>
                </Field>
              </div>

              <div className="pt-2">
                <button
                  data-testid="nri-form-submit"
                  type="submit"
                  disabled={loading}
                  className="inline-flex items-center gap-3 px-8 py-4 bg-[#1A362D] text-[#F7F5F0] text-[12px] tracking-[0.22em] uppercase font-semibold hover:bg-[#987F53] transition-colors disabled:opacity-60"
                >
                  {loading ? "Sending..." : "Speak to an Advisor"}
                  <ArrowRight className="w-4 h-4" />
                </button>
              </div>

              <p className="text-xs text-[#1A362D]/50 font-light leading-relaxed pt-2">
                By submitting, you consent to be contacted by Amarja Hills.{" "}
                <Link to="/privacy-policy" className="underline hover:text-[#987F53]">Privacy Policy</Link>.
              </p>
            </form>
          )}
        </div>
      </div>
    </section>
  );
}

// -------- Footer (dark forest, matches main site footer feel) --------
function NriFooter({ onSchedule }) {
  return (
    <footer data-testid="nri-footer" className="relative bg-[#1A362D] pt-20 pb-8 text-[#F7F5F0]">
      <div className="max-w-[100rem] mx-auto px-6 md:px-10 lg:px-16 grid grid-cols-1 lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <img src={SITE.logo} alt="Amarja Hills" className="h-12 w-auto logo-on-dark" />
          <p className="mt-5 max-w-md text-[#F7F5F0]/70 leading-relaxed font-light text-sm">
            A 500-acre private hill estate overlooking Pawna Lake.
          </p>
        </div>
        <div className="lg:col-span-3">
          <p className="text-[10px] tracking-[0.28em] uppercase text-[#E2CFA1] font-semibold mb-4">International Enquiries</p>
          <a data-testid="nri-footer-phone" href={`tel:${SITE.telHref}`} className="font-serif-display text-xl text-[#F7F5F0] hover:text-[#E2CFA1] transition-colors font-light block">
            {SITE.phone}
          </a>
          <a data-testid="nri-footer-email" href={`mailto:${SITE.email}`} className="block mt-2 text-[#F7F5F0]/70 hover:text-[#F7F5F0] transition-colors text-sm">
            {SITE.email}
          </a>
          <a data-testid="nri-footer-whatsapp" href={`https://wa.me/${SITE.whatsapp}`} target="_blank" rel="noopener noreferrer" className="mt-3 inline-flex items-center gap-2 text-[#F7F5F0]/70 hover:text-[#E2CFA1] transition-colors text-sm">
            <WhatsAppIcon className="w-4 h-4" /> WhatsApp
          </a>
        </div>
        <div className="lg:col-span-4 flex flex-col gap-3">
          <p className="text-[10px] tracking-[0.28em] uppercase text-[#E2CFA1] font-semibold mb-1">Next Steps</p>
          <button data-testid="nri-footer-zoom" onClick={onSchedule} className="text-left inline-flex items-center gap-3 py-2.5 border-b border-[#E2CFA1]/15 hover:border-[#E2CFA1] text-[#F7F5F0] hover:text-[#E2CFA1] transition-colors text-sm">
            <Video className="w-4 h-4 text-[#E2CFA1]" strokeWidth={1.6} /> Schedule Zoom Consultation
          </button>
          <a data-testid="nri-footer-brochure" href={NRI_SITE.brochureUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 py-2.5 border-b border-[#E2CFA1]/15 hover:border-[#E2CFA1] text-[#F7F5F0] hover:text-[#E2CFA1] transition-colors text-sm">
            <Download className="w-4 h-4 text-[#E2CFA1]" strokeWidth={1.6} /> Download Brochure
          </a>
          <Link to="/" data-testid="nri-footer-main-site" className="inline-flex items-center gap-3 py-2.5 border-b border-[#E2CFA1]/15 hover:border-[#E2CFA1] text-[#F7F5F0] hover:text-[#E2CFA1] transition-colors text-sm">
            <ChevronDown className="w-4 h-4 text-[#E2CFA1] rotate-[-90deg]" strokeWidth={1.6} /> Main Amarja Hills Site
          </Link>
        </div>
      </div>
      <div className="max-w-[100rem] mx-auto px-6 md:px-10 lg:px-16 mt-16 pt-6 border-t border-[#E2CFA1]/12 flex flex-col md:flex-row md:items-center md:justify-between gap-3 text-xs text-[#F7F5F0]/50">
        <p>© {new Date().getFullYear()} Amarja Hills · Nanak Properties Pvt Ltd</p>
        <div className="flex items-center gap-6">
          <Link to="/privacy-policy" className="hover:text-[#E2CFA1] transition-colors">Privacy Policy</Link>
          <span className="tracking-[0.22em] uppercase">A Private Legacy Offering</span>
        </div>
      </div>
    </footer>
  );
}

// -------- Sticky Consultation --------
function StickyConsult({ onClick }) {
  const [show, setShow] = useState(false);
  useEffect(() => {
    const on = () => setShow(window.scrollY > window.innerHeight * 0.6);
    on();
    window.addEventListener("scroll", on, { passive: true });
    return () => window.removeEventListener("scroll", on);
  }, []);
  return (
    <button
      data-testid="nri-sticky-consult"
      onClick={onClick}
      className={`fixed left-4 md:left-8 bottom-6 md:bottom-10 z-40 inline-flex items-center gap-2 px-5 md:px-6 py-3 md:py-3.5 bg-[#1A362D] text-[#F7F5F0] text-[11px] tracking-[0.22em] uppercase font-semibold shadow-[0_20px_50px_rgba(26,54,45,0.35)] hover:bg-[#987F53] transition-all ${
        show ? "translate-y-0 opacity-100" : "translate-y-8 opacity-0 pointer-events-none"
      }`}
    >
      <Video className="w-4 h-4" strokeWidth={2} />
      Schedule Consultation
    </button>
  );
}

// -------- Page --------
export default function NriHome() {
  const formRef = useRef(null);

  useEffect(() => {
    const prev = document.title;
    document.title = NRI_SITE.metaTitle;
    let md = document.querySelector('meta[name="description"]');
    const hadMd = !!md;
    const prevMd = md?.getAttribute("content") || null;
    if (!md) {
      md = document.createElement("meta");
      md.setAttribute("name", "description");
      document.head.appendChild(md);
    }
    md.setAttribute("content", NRI_SITE.metaDescription);
    let mr = document.querySelector('meta[name="robots"]');
    const hadMr = !!mr;
    const prevMr = mr?.getAttribute("content") || null;
    if (!mr) {
      mr = document.createElement("meta");
      mr.setAttribute("name", "robots");
      document.head.appendChild(mr);
    }
    mr.setAttribute("content", "index,follow");
    window.scrollTo({ top: 0, behavior: "instant" });

    return () => {
      document.title = prev;
      if (!hadMd) md?.parentNode?.removeChild(md);
      else if (prevMd !== null) md.setAttribute("content", prevMd);
      if (!hadMr) mr?.parentNode?.removeChild(mr);
      else if (prevMr !== null) mr.setAttribute("content", prevMr);
    };
  }, []);

  const scrollToForm = () => {
    if (formRef.current) formRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div data-testid="nri-page" className="bg-[#F7F5F0] text-[#1A362D] min-h-screen">
      <NriHeader onSchedule={scrollToForm} />
      <main>
        <NriHero onSchedule={scrollToForm} />
        <NriWhyStrip />
        <NriShowcase />
        <NriPerformance />
        <NriCurrencyCalculator />
        <NriCinematicGallery />
        <NriMap />
        <NriLifestyle />
        <NriTestimonials />
        <NriFaq />
        <NriLeadForm formRef={formRef} />
      </main>
      <NriFooter onSchedule={scrollToForm} />
      <StickyConsult onClick={scrollToForm} />
    </div>
  );
}
